import * as types from '../mutation-types';
import DatabaseConnectionsAPI from '../../api/externalDatabaseConnectionsApi';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: {
    tables: [],
    tableData: {},
    tableDescription: {},
    queryResults: {}, // Unificado para todos os tipos de query
    whatsappMessageLimit: null,
  },
  uiFlags: {
    isFetchingTables: false,
    isFetchingTableData: false,
    isFetchingTableDescription: false,
    isFetchingQueryResults: false, // Flag unificada
    isFetchingWhatsappMessageLimit: false,
  },
};

export const getters = {
  getTables($state) {
    return $state.records.tables || [];
  },
  getTableData: $state => tableName => {
    return $state.records.tableData?.[tableName] || [];
  },

  getTableDescription: $state => tableName => {
    return $state.records.tableDescription?.[tableName] || [];
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },

  // Getter específico para colunas de telefone
  getTablePhoneColumns: $state => tableName => {
    return $state.records.tableDescription?.[`${tableName}_phone_columns`] || [];
  },

  // Getter específico para colunas de nome
  getTableNameColumns: $state => tableName => {
    return $state.records.tableDescription?.[`${tableName}_name_columns`] || [];
  },

  // Getter para colunas com filtros combinados
  getTableColumnsWithFilters: $state => (tableName, filterType) => {
    const key = filterType ? `${tableName}_${filterType}` : tableName;
    return $state.records.tableDescription?.[key] || [];
  },

  // Getter unificado para query results
  getQueryResults: $state => tableName => {
    return $state.records.queryResults?.[tableName] || { rows: [], total_count: 0 };
  },
  // Getters legacy para compatibilidade
  getFilteredTableData: $state => tableName => {
    return $state.records.queryResults?.[tableName]?.rows || [];
  },
  getLimitedTableData: $state => tableName => {
    return $state.records.queryResults?.[tableName] || { rows: [], total_count: 0 };
  },
  getWhatsappMessageLimit($state) {
    return $state.records.whatsappMessageLimit || null;
  },
};

export const actions = {
  // Lista todas as tabelas da conexão de banco de dados
  listTables: async ({ commit }) => {
    commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTables: true });
    try {
      const response = await DatabaseConnectionsAPI.listTables();
      commit(types.default.SET_TABLES, response.data.data);
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTables: false });
      return response.data.data;
    } catch (error) {
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTables: false });
      throwErrorMessage(error);
    }
  },

  // Descreve a estrutura completa de uma tabela (sem filtros)
  describeTable: async ({ commit }, { tableName }) => {
    commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: true });
    try {
      const response = await DatabaseConnectionsAPI.describeTable(tableName);
      commit(types.default.SET_TABLE_DESCRIPTION, { tableName, columns: response.data.data });
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: false });
      return response.data.data;
    } catch (error) {
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: false });
      throwErrorMessage(error);
    }
  },

  // Busca apenas colunas que podem conter números de telefone
  describeTablePhoneColumns: async ({ commit }, { tableName }) => {
    commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: true });
    try {
      const response = await DatabaseConnectionsAPI.describeTable(tableName, {
        filterPhoneColumns: true
      });
      commit(types.default.SET_TABLE_DESCRIPTION, {
        tableName: `${tableName}_phone_columns`,
        columns: response.data.data
      });
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: false });
      return response.data.data;
    } catch (error) {
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: false });
      throwErrorMessage(error);
    }
  },

  // Busca apenas colunas que podem conter nomes/contatos
  describeTableNameColumns: async ({ commit }, { tableName }) => {
    commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: true });
    try {
      const response = await DatabaseConnectionsAPI.describeTable(tableName, {
        filterNameColumns: true
      });
      commit(types.default.SET_TABLE_DESCRIPTION, {
        tableName: `${tableName}_name_columns`,
        columns: response.data.data
      });
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: false });
      return response.data.data;
    } catch (error) {
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: false });
      throwErrorMessage(error);
    }
  },

  // Busca colunas com filtros customizados (máxima flexibilidade)
  describeTableWithFilters: async ({ commit }, { tableName, filterPhoneColumns = false, filterNameColumns = false }) => {
    commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: true });
    try {
      const response = await DatabaseConnectionsAPI.describeTable(tableName, {
        filterPhoneColumns,
        filterNameColumns
      });

      // Criar chave única baseada nos filtros aplicados
      let tableKey = tableName;
      if (filterPhoneColumns && filterNameColumns) {
        tableKey = `${tableName}_phone_name_columns`;
      } else if (filterPhoneColumns) {
        tableKey = `${tableName}_phone_columns`;
      } else if (filterNameColumns) {
        tableKey = `${tableName}_name_columns`;
      }

      commit(types.default.SET_TABLE_DESCRIPTION, {
        tableName: tableKey,
        columns: response.data.data
      });
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: false });
      return response.data.data;
    } catch (error) {
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingTableDescription: false });
      throwErrorMessage(error);
    }
  },

  // Método unificado para consultar tabela
  queryTable: async ({ commit }, { tableName, options = {} }) => {
    commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingQueryResults: true });
    try {
      const response = await DatabaseConnectionsAPI.queryTable(tableName, options);

      // ✅ NOVA ESTRUTURA: response.data contém { data: [...], metadata: {...}, message: "..." }
      commit(types.default.SET_QUERY_RESULTS, {
        tableName,
        data: response.data // Armazenar estrutura completa no store
      });

      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingQueryResults: false });

      // ✅ Retornar estrutura completa (data + metadata + message)
      return response.data;
    } catch (error) {
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingQueryResults: false });
      throwErrorMessage(error);
    }
  },

  // Actions legacy para compatibilidade (chamam o método unificado)
  showTable: async ({ dispatch }, { tableName }) => {
    return dispatch('queryTable', {
      tableName,
      options: {
        filters: [],
        maxRecords: 100,
        includeCount: false,
      }
    });
  },

  selectTableWithFilters: async ({ dispatch }, { tableName, filters, columns = ['*'], page = 1, per_page = 500 }) => {
    return dispatch('queryTable', {
      tableName,
      options: {
        filters,
        columns,
        page,
        perPage: per_page,
        includeCount: false,
      }
    });
  },

  fetchTableWithLimitAndCount: async ({ dispatch }, { tableName, filters, columns = ['*'], max_records = 1000 }) => {
    return dispatch('queryTable', {
      tableName,
      options: {
        filters,
        columns,
        maxRecords: max_records,
        includeCount: true,
      }
    });
  },

  // Obtém o limite de mensagens do WhatsApp
  // payload: inboxId, ou { inboxId, refresh } quando precisa ir na Meta.
  fetchWhatsappMessageLimit: async ({ commit }, payload) => {
    const { inboxId, refresh } =
      typeof payload === 'object' && payload !== null
        ? payload
        : { inboxId: payload };
    commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingWhatsappMessageLimit: true });
    try {
      const response = await DatabaseConnectionsAPI.fetchWhatsappMessageLimit(
        inboxId,
        { refresh }
      );
      commit(types.default.SET_WHATSAPP_MESSAGE_LIMIT, response.data.data);
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingWhatsappMessageLimit: false });
      return response.data.data;
    } catch (error) {
      commit(types.default.SET_DATABASE_CONNECTIONS_UI_FLAG, { isFetchingWhatsappMessageLimit: false });
      throwErrorMessage(error);
    }
  },
};

export const mutations = {
  [types.default.SET_DATABASE_CONNECTIONS_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },

  [types.default.SET_TABLES]($state, tables) {
    $state.records = { ...$state.records, tables };
  },

  [types.default.SET_TABLE_DATA]($state, { tableName, data }) {
    $state.records = {
      ...$state.records,
      tableData: {
        ...$state.records.tableData,
        [tableName]: data,
      },
    };
  },

  [types.default.SET_TABLE_DESCRIPTION]($state, { tableName, columns }) {
    $state.records = {
      ...$state.records,
      tableDescription: {
        ...$state.records.tableDescription,
        [tableName]: columns,
      },
    };
  },

  // Mutation unificada para query results
  [types.default.SET_QUERY_RESULTS]($state, { tableName, data }) {
    $state.records = {
      ...$state.records,
      queryResults: {
        ...$state.records.queryResults,
        [tableName]: data,
      },
    };
  },

  // Mutations legacy para compatibilidade (chamam a unificada)
  [types.default.SET_FILTERED_TABLE_DATA]($state, { tableName, data }) {
    $state.records = {
      ...$state.records,
      queryResults: {
        ...$state.records.queryResults,
        [tableName]: { rows: data, total_count: data.length },
      },
    };
  },

  [types.default.SET_LIMITED_TABLE_DATA]($state, { tableName, data }) {
    $state.records = {
      ...$state.records,
      queryResults: {
        ...$state.records.queryResults,
        [tableName]: data,
      },
    };
  },

  [types.default.SET_WHATSAPP_MESSAGE_LIMIT]($state, data) {
    $state.records = {
      ...$state.records,
      whatsappMessageLimit: data,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};